import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface AnalyticsStats {
  totalViews: number;
  uniqueSessions: number;
  avgSessionDuration: number;
  topPages: Array<{ page: string; views: number }>;
}

export const AnalyticsDashboard = () => {
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        // Get total views and unique sessions from last 30 days
        const { data: views, error } = await supabase
          .from('page_views')
          .select('*')
          .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

        if (error) throw error;

        if (!views) {
          setStats({
            totalViews: 0,
            uniqueSessions: 0,
            avgSessionDuration: 0,
            topPages: []
          });
          return;
        }

        // Calculate stats
        const totalViews = views.length;
        const uniqueSessions = new Set(views.map(v => v.session_id)).size;

        // Calculate top pages
        const pageCounts = views.reduce((acc, view) => {
          acc[view.page_path] = (acc[view.page_path] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        const topPages = Object.entries(pageCounts)
          .map(([page, views]) => ({ page, views }))
          .sort((a, b) => b.views - a.views)
          .slice(0, 10);

        // Calculate average session duration (simplified)
        const sessionTimes: Record<string, number[]> = {};
        views.forEach(view => {
          if (!sessionTimes[view.session_id]) {
            sessionTimes[view.session_id] = [];
          }
          sessionTimes[view.session_id].push(new Date(view.created_at).getTime());
        });

        const durations = Object.values(sessionTimes).map(times => {
          if (times.length < 2) return 0;
          return Math.max(...times) - Math.min(...times);
        });

        const avgSessionDuration = durations.length > 0
          ? durations.reduce((a, b) => a + b, 0) / durations.length / 1000 / 60 // Convert to minutes
          : 0;

        setStats({
          totalViews,
          uniqueSessions,
          avgSessionDuration: Math.round(avgSessionDuration * 10) / 10,
          topPages
        });
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  if (loading) {
    return <div className="p-8">Lade Analytics...</div>;
  }

  if (!stats) {
    return <div className="p-8">Keine Daten verfügbar</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
      <p className="text-muted-foreground">Letzte 30 Tage • Anonymisiert • DSGVO-konform</p>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Seitenaufrufe</CardTitle>
            <CardDescription>Gesamt</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{stats.totalViews.toLocaleString('de-DE')}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Eindeutige Sitzungen</CardTitle>
            <CardDescription>Unique Sessions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{stats.uniqueSessions.toLocaleString('de-DE')}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ø Sitzungsdauer</CardTitle>
            <CardDescription>Minuten</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{stats.avgSessionDuration}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Top 10 Seiten</CardTitle>
          <CardDescription>Meist besuchte Unterseiten</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={stats.topPages}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="page" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="views" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};
